/* eslint-disable @typescript-eslint/no-explicit-any */
import { IComparableProperty } from '@/components/ComparableSales';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { uniqueId } from 'lodash';
import useGetProfile from './useGetProfile';
import getApiBaseUrl from '@/utils/getBaseUrl';

interface Payload {
  comparableSales: IComparableProperty[];
  address: string;
  name: string;
  emailAddress: string;
  phoneNumber: string;
  apn?: string;
  state: string;
  fairMarketValue?: string;
  officialValuation?: string;
  legalDescription?: string;
  accountId?: string;
  assessor: {
    countyName: string;
    address: string;
    phone?: string;
    websiteUrl?: string;
    protestUrl?: string;
  };
  aiComparableSales?: IComparableProperty[];
  yearBuilt?: string;
  soldPrice?: number;
  images?: string[];
}

export default function useCreateReport() {
  const queryClient = useQueryClient();
  const { data: profile } = useGetProfile();

  const token = window.localStorage.getItem('_token');

  const propertyInfo = JSON.parse(
    window.localStorage.getItem('propertyInfo') || null,
  );

  return useMutation({
    mutationFn: async (payload: Payload) => {
      queryClient.setQueryData(['REPORTS'], (prev: any) => {
        const newReport = {
          accountId: profile.auth0Id,
          address: payload.address,
          createdAt: dayjs().toISOString(),
          updatedAt: dayjs().toISOString(),
          _id: uniqueId(),
          status: 'Processing',
        };

        if (!prev) {
          return [newReport];
        } else {
          return [newReport, ...prev];
        }
      });

      const response = await fetch(`${getApiBaseUrl()}/api/v1/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...payload, propertyInfo }),
      });

      window.localStorage.removeItem('comparableProperties');
      window.localStorage.removeItem('aiProperties');
      window.localStorage.removeItem('propertyInfo');
      window.localStorage.removeItem('subjectPropertyImages');
      window.localStorage.removeItem('assessorInfo');
      window.localStorage.removeItem('fairMarketValue');
      window.sessionStorage.removeItem('search');

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.statusText}`);
      }

      return (await response.json()).data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['REPORTS'] });
    },
  });
}
